import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  Flex,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaStepForward,
  FaStepBackward,
  FaSyncAlt,
} from 'react-icons/fa';
import { IoMdRepeat } from 'react-icons/io';
import { MdRepeatOneOn } from 'react-icons/md';

const tracks = [
  { name: 'Lofi Lofi Song', src: '/sounds/lofi-lofi-song-374862.mp3' },
  {
    name: 'Rainy Lofi City - Lofi Music',
    src: '/sounds/rainy-lofi-city-lofi-music-332746.mp3',
  },
  {
    name: 'Coffee Lofi - Chill Lofi Music',
    src: '/sounds/coffee-lofi-chill-lofi-music-332738.mp3',
  },
];

type LoopMode = 'none' | 'single' | 'all';

export const BgmPlayer = () => {
  const [trackIndex, setTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [loopMode, setLoopMode] = useState<LoopMode>('all');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.warn('play error', err));
    }
  };

  const changeTrack = (direction: 'next' | 'prev') => {
    const nextIndex =
      direction === 'next'
        ? (trackIndex + 1) % tracks.length
        : (trackIndex - 1 + tracks.length) % tracks.length;
    setTrackIndex(nextIndex);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.load();
    audio.volume = volume;
    audio.loop = loopMode === 'single';

    if (isPlaying) {
      audio
        .play()
        .catch((err) => console.warn('play error after track change', err));
    }
  }, [trackIndex, loopMode]);

  const handleAudioEnd = () => {
    if (loopMode === 'all') {
      changeTrack('next');
    }
    // loopMode === 'single' は audio.loop=true で自動再生継続
    // loopMode === 'none' は何もしない（停止）
  };

  const toggleLoopMode = () => {
    setLoopMode((prev) => {
      if (prev === 'none') return 'single';
      if (prev === 'single') return 'all';
      return 'none';
    });
  };

  const getLoopModeLabel = () => {
    if (loopMode === 'single') return 'Loop 1';
    if (loopMode === 'all') return 'Loop All';
    return 'Loop Off';
  };

  return (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      width="100%"
      bg="gray.200"
      py={2}
      px={3}
      boxShadow="md"
      zIndex={1000}
    >
      <Flex wrap="wrap" gap={3} justify="center" align="center">
        <IconButton
          aria-label="Previous"
          icon={<FaStepBackward />}
          onClick={() => changeTrack('prev')}
          variant="ghost"
          size="sm"
        />
        <IconButton
          aria-label={isPlaying ? 'Pause' : 'Play'}
          icon={isPlaying ? <FaPause /> : <FaPlay />}
          onClick={togglePlay}
          variant="ghost"
          size="sm"
        />
        <IconButton
          aria-label="Next"
          icon={<FaStepForward />}
          onClick={() => changeTrack('next')}
          variant="ghost"
          size="sm"
        />
        <IconButton
          aria-label="Volume"
          icon={<FaVolumeUp />}
          variant="ghost"
          size="sm"
        />
        <Slider
          aria-label="Volume"
          value={volume * 100}
          onChange={(val) => {
            const v = val / 100;
            setVolume(v);
            if (audioRef.current) {
              audioRef.current.volume = v;
            }
          }}
          width={{ base: '100px', sm: '120px', md: '150px' }}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb boxSize={4} />
        </Slider>
        {/* <IconButton
          aria-label="Loop Mode"
          icon={<FaSyncAlt />}
          onClick={toggleLoopMode}
          colorScheme={
            loopMode === 'none'
              ? 'gray'
              : loopMode === 'single'
              ? 'blue'
              : 'green'
          }
          variant="ghost"
          size="sm"
          title={getLoopModeLabel()}
        /> */}
        <IconButton
          aria-label="Loop Mode"
          icon={
            loopMode === 'single' ? (
              <MdRepeatOneOn />
            ) : loopMode === 'all' ? (
              <IoMdRepeat />
            ) : (
              <FaSyncAlt />
            )
          }
          onClick={toggleLoopMode}
          variant="ghost"
          size="sm"
          title={getLoopModeLabel()}
        />
        <Text
          fontSize="xs"
          color="gray.600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxW={{ base: '120px', sm: '180px', md: '240px' }}
        >
          {tracks[trackIndex].name}
        </Text>
      </Flex>
      <audio
        ref={audioRef}
        src={tracks[trackIndex].src}
        onEnded={handleAudioEnd}
      />
    </Box>
  );
};
